'use client';

import { useDirection } from '@repo/react-hooks';
import { Carousel, type CarouselApi, CarouselContent, CarouselItem } from '@repo/ui/carousel';
import { TabItem } from '@repo/ui/tab-item';
import { cn } from '@repo/utils/cn';
import { type VariantProps, cva } from 'class-variance-authority';
import { type ReactNode, useMemo, useRef } from 'react';
import { UnStyledLink } from '@/components/common/link';
import InlineItemsSkeleton from '@/components/common/skeletons/inline-items-skeleton';
import { type GamesCategoryItem } from '@/lib/utils/casino-categories';

const scrollableTabItemsVariants = cva('relative flex w-full items-center gap-2.5 rounded py-[10px]', {
  variants: {
    variant: {
      border: 'rounded border border-[var(--colors-dark-500)] pl-[10px]',
    },
  },
});

interface ScrollableTabItemsProps extends VariantProps<typeof scrollableTabItemsVariants> {
  orientation: 'vertical' | 'horizontal';
  pathname: string;
  data: GamesCategoryItem[];
  placeholderComponent?: ReactNode;
  className?: string;
  getTabClassName?: (isActive: boolean) => string;
  testId?: string;
  loading?: boolean;
  onClick?: (item: GamesCategoryItem, api: CarouselApi, index: number) => void;
}

const ScrollableTabItems = ({
  data,
  placeholderComponent,
  testId,
  pathname,
  orientation,
  variant,
  getTabClassName,
  className,
  loading,
  onClick,
}: ScrollableTabItemsProps) => {
  const direction = useDirection();

  const startIndex = data.findIndex(({ matches }) => matches?.some((match) => pathname.startsWith(match)));

  const currentIndex = useMemo(() => {
    return data.findIndex(({ href }) => href === pathname);
  }, [pathname]);

  const initialStartIndex = useRef(startIndex);

  return (
    <section
      className={cn(scrollableTabItemsVariants({ variant }), className)}
      data-testid={testId}
    >
      <div className="flex w-full">
        <Carousel
          className="w-full"
          currentIndex={currentIndex}
          opts={{
            direction,
            startIndex: initialStartIndex.current,
            dragThreshold: 1,
            dragFree: true,
          }}
        >
          <CarouselContent>
            {data.map((item, index) => {
              const { href, name, Icon, matches, notMatches } = item;
              const firstItem = index === 0;
              const lastItem = index === data.length - 1;
              const isActive =
                !(notMatches ?? [])?.some((match) => pathname.startsWith(match)) &&
                (matches ?? [])?.some((match) => pathname.startsWith(match));
              return (
                <CarouselItem
                  key={index}
                  className={cn('basis-auto rtl:pl-1.5', {
                    'ltr:pl-1.5': !firstItem,
                    'ltr:pr-1.5 rtl:pl-1.5': lastItem,
                    'ltr:pr-2 rtl:pl-2': loading && data.length,
                  })}
                  onItemClick={(api) => onClick && onClick(item, api, index)}
                >
                  <TabItem
                    orientation={orientation}
                    isActive={isActive}
                    className={getTabClassName && getTabClassName(isActive)}
                    asChild
                  >
                    <UnStyledLink href={!onClick ? href : ''}>
                      <Icon
                        className="size-[18px]"
                        {...(Icon as React.HTMLAttributes<SVGElement>)}
                      />
                      <span
                        suppressHydrationWarning
                        className="vt-font-highlight-font-family vt-text-size-medium sm:vt-text-size-medium-sm"
                      >
                        {name}
                      </span>
                    </UnStyledLink>
                  </TabItem>
                </CarouselItem>
              );
            })}
            {loading && (
              <InlineItemsSkeleton
                itemsCount={5}
                itemClassName="h-10"
                className="py-0"
              />
            )}
          </CarouselContent>
        </Carousel>
      </div>
      {!!placeholderComponent && (
        <div className="absolute m-auto flex h-full items-center ltr:right-[-1px] rtl:left-[-1px]">
          <div className="relative">{placeholderComponent}</div>
        </div>
      )}
    </section>
  );
};

export default ScrollableTabItems;
